import { Box, TextField, Typography } from '@mui/material';
import React, { useCallback } from 'react';
import { useContextSelection } from '../hooks/useContextSelection';
import { freeTextContextStyles as styles } from '../styles/FreeTextContextStyles';

interface FreeTextContextProps {
  value?: string;
  onChange?: (value: string) => void;
  maxCharacters?: number;
}

const CHARACTER_LIMIT = 3000;
const FREE_TEXT_CONTEXT_KEY = 'freeTextContext';

export const FreeTextContext: React.FC<FreeTextContextProps> = ({
  value: propValue,
  onChange: propOnChange,
  maxCharacters = CHARACTER_LIMIT,
}) => {
  const { contextData, updateContextData } = useContextSelection();

  const freeTextItem = contextData.find(
    (item) => item.key === FREE_TEXT_CONTEXT_KEY
  );
  const sessionValue = (freeTextItem?.value as string) || '';

  const isControlledByProp = propOnChange !== undefined;

  const currentValue = propValue !== undefined ? propValue : sessionValue;

  const characterCount = currentValue.length;
  const isNearLimit = characterCount > maxCharacters * 0.9;
  const isAtLimit = characterCount >= maxCharacters;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      if (newValue.length <= maxCharacters) {
        if (isControlledByProp && propOnChange) {
          propOnChange(newValue);
        } else {
          updateContextData(FREE_TEXT_CONTEXT_KEY, newValue, { hidden: true });
        }
      }
    },
    [isControlledByProp, propOnChange, maxCharacters, updateContextData]
  );

  return (
    <Box sx={styles.container}>
      <Typography variant='body2' sx={styles.label}>
        Write any additional context to include:
      </Typography>

      <TextField
        multiline
        fullWidth
        rows={4}
        value={currentValue}
        onChange={handleChange}
        variant='outlined'
        placeholder='Enter additional context here...'
        sx={styles.textField}
        inputProps={{
          maxLength: maxCharacters,
          'aria-label': 'Additional context input',
        }}
      />

      <Typography
        variant='caption'
        sx={
          isNearLimit || isAtLimit
            ? styles.characterCountWarning
            : styles.characterCount
        }
      >
        {characterCount.toLocaleString()} / {maxCharacters.toLocaleString()}{' '}
        characters
      </Typography>
    </Box>
  );
};
